// utils/validation.ts

// Type for validation options for string validation
interface ValidateStringOptions {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
}

// Validate if a string meets the criteria
export const validateString = (
  value: string | undefined,
  fieldName: string,
  { required = false, minLength, maxLength, pattern }: ValidateStringOptions = {},
): string => {
  // Check if the field is required and empty
  if (required && (!value || value.trim() === '')) {
    return `${fieldName} is required.`
  }

  // If value is undefined, return an empty string (no validation error)
  if (value === undefined) {
    return ''
  }

  // Check for minimum length
  if (minLength !== undefined && value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters long.`
  }

  // Check for maximum length
  if (maxLength !== undefined && value.length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters long.`
  }

  // Check for custom pattern (like alphanumeric or specific formats)
  if (pattern && !pattern.test(value)) {
    return `${fieldName} has an invalid format.`
  }

  // If all validations pass, return an empty string
  return ''
}

// Type for validation options for number validation
interface ValidateNumberOptions {
  required?: boolean
  positive?: boolean
  negative?: boolean
  min?: number
  max?: number
}

// Validate if a number meets the criteria
export const validateNumber = (
  value: any, // `any` allows flexibility for different types
  fieldName: string,
  { required = false, positive = false, negative = false, min, max }: ValidateNumberOptions = {},
): string => {
  // Check if the field is required and empty (but allow 0 if not required)
  if (required && (value === '' || value === null || value === undefined)) {
    return `${fieldName} is required.`
  }

  // Convert value to a number (keep 0 as valid input)
  const numberValue = value !== '' && value !== null && value !== undefined ? Number(value) : 0

  // Check if it's a valid number (ensure empty is not validated as 0)
  if (isNaN(numberValue)) {
    return `${fieldName} must be a valid number.`
  }

  // Check for whole number
  if (!Number.isInteger(numberValue)) {
    return `${fieldName} must be a whole number.`
  }

  // Check for positive number
  if (positive && numberValue < 0) {
    return `${fieldName} must be a positive number.`
  }

  // Check for negative number
  if (negative && numberValue > 0) {
    return `${fieldName} must be a negative number.`
  }

  // Check for minimum value
  if (min !== undefined && numberValue < min) {
    return `${fieldName} must be greater than or equal to ${min}.`
  }

  // Check for maximum value
  if (max !== undefined && numberValue > max) {
    return `${fieldName} must be less than or equal to ${max}.`
  }

  // If all validations pass, return an empty string
  return ''
}

export interface ValidateURLOptions {
  required?: boolean
  exampleUrl?: string
}

export const validateURL = (
  value: string | undefined,
  fieldName: string,
  { required = false, exampleUrl = 'https://www.example.com' }: ValidateURLOptions = {},
): string => {
  // Check if the field is required and empty
  if (required && (!value || value.trim() === '')) {
    return `${fieldName} is required.`
  }

  // If value is undefined, return an empty string (no validation error)
  if (value === undefined || value === '') {
    return ''
  }

  // Regular expression to match a valid URL
  const urlPattern = /^https:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/

  // Check if the URL is valid
  if (!urlPattern.test(value)) {
    return `${fieldName} must be a valid URL. Ex: ${exampleUrl}` // Use the example URL provided
  }

  // If all validations pass, return an empty string
  return ''
}

// Type for validation options for date validation
interface ValidateDateOptions {
  required?: boolean
  future?: boolean
  past?: boolean
  minDate?: string
  maxDate?: string
}

// Validate if a value is a valid date
export const validateDate = (
  value: string | undefined,
  fieldName: string,
  { required = false, future = false, past = false, minDate, maxDate }: ValidateDateOptions = {},
): string => {
  // Check if the field is required and empty
  if (required && (!value || isNaN(new Date(value).getTime()))) {
    return `${fieldName} is required.`
  }

  // If value is undefined, return an empty string (no validation error)
  if (value === undefined) {
    return ''
  }

  const dateValue = new Date(value)

  // Check if the input is a valid date
  if (isNaN(dateValue.getTime())) {
    return `${fieldName} must be a valid date.`
  }

  const currentDate = new Date()

  // Check if the date must be in the future
  if (future && dateValue <= currentDate) {
    return `${fieldName} must be in the future.`
  }

  // Check if the date must be in the past
  if (past && dateValue >= currentDate) {
    return `${fieldName} must be in the past.`
  }

  // Check for minimum date
  if (minDate && dateValue < new Date(minDate)) {
    return `${fieldName} must be on or after ${new Date(minDate).toLocaleDateString()}.`
  }

  // Check for maximum date
  if (maxDate && dateValue > new Date(maxDate)) {
    return `${fieldName} must be on or before ${new Date(maxDate).toLocaleDateString()}.`
  }

  // If all validations pass, return an empty string
  return ''
}

// Type for validation options for array validation
export const validateArray = (
  value: any[],
  fieldName: string,
  {
    required = false,
    minLength,
    maxLength,
    allowDuplicates = true,
    elementValidation,
  }: {
    required?: boolean
    minLength?: number
    maxLength?: number
    allowDuplicates?: boolean
    elementValidation?: (element: any, fieldName: string) => string
  } = {},
): string => {
  // Check if the field is required and empty
  if (required && (!value || !Array.isArray(value) || value.length === 0)) {
    return `${fieldName} is required.`
  }

  // If value is undefined, return an empty string (no validation error)
  if (!Array.isArray(value)) {
    return ''
  }

  // Check for minimum length
  if (minLength !== undefined && value.length < minLength) {
    return `${fieldName} must contain at least ${minLength} items.`
  }

  // Check for maximum length
  if (maxLength !== undefined && value.length > maxLength) {
    return `${fieldName} must contain no more than ${maxLength} items.`
  }

  // Check for duplicates if not allowed
  if (!allowDuplicates) {
    const uniqueItems = new Set(value)
    if (uniqueItems.size !== value.length) {
      return `${fieldName} must not contain duplicate items.`
    }
  }

  // Validate each element in the array (if provided)
  if (elementValidation && Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const elementError = elementValidation(value[i], `${fieldName} item ${i + 1}`)
      if (elementError) {
        return elementError // Return first validation error found
      }
    }
  }

  // If all validations pass, return an empty string
  return ''
}
